import { doc, getDoc, setDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Default branding configuration for backwards compatibility
 */
export const DEFAULT_BRANDING = {
  companyName: 'FusionICT Billing',
  tagline: 'Cloud Infrastructure Services',
  logoUrl: null,
  primaryColor: '#2962ff',
  secondaryColor: '#2d3748',
  accentColor: '#e53e3e',
  footerText: 'Thank you for your business!',
  emailCcRecipients: ['roseller@fusionict.com'],
  paymentInstructions: 'GCash: Roseller Armirola 0967-234-5386',
  paymentTerms: 'Payment is due within 15 days of invoice date',
  invoiceNumberPrefix: 'INV',
  emailFromName: 'FusionICT Billing',
  emailSignature: 'Best regards,\nThe FusionICT Team'
};

/**
 * Validates hex color format
 * @param {string} color - Color string to validate
 * @returns {boolean} True if valid hex color
 */
export const isValidHexColor = (color) => {
  return /^#[0-9A-Fa-f]{6}$/.test(color);
};

/**
 * Sanitizes HTML content to prevent XSS
 * Allows basic formatting tags only
 * @param {string} html - HTML string to sanitize
 * @returns {string} Sanitized HTML
 */
export const sanitizeHtml = (html) => {
  if (!html) return '';

  // Allow only safe tags
  const allowedTags = ['p', 'br', 'strong', 'em', 'b', 'i', 'ul', 'ol', 'li', 'a'];

  // Remove script tags and event handlers
  let sanitized = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  sanitized = sanitized.replace(/on\w+\s*=\s*["'][^"']*["']/gi, '');
  sanitized = sanitized.replace(/javascript:/gi, '');

  return sanitized;
};

/**
 * Validates branding data before saving
 * @param {Object} branding - Branding data to validate
 * @returns {Object} Validation result { valid: boolean, errors: string[] }
 */
export const validateBranding = (branding) => {
  const errors = [];

  if (branding.companyName && branding.companyName.length > 100) {
    errors.push('Company name must be 100 characters or less');
  }

  if (branding.tagline && branding.tagline.length > 200) {
    errors.push('Tagline must be 200 characters or less');
  }

  if (branding.primaryColor && !isValidHexColor(branding.primaryColor)) {
    errors.push('Primary color must be a valid hex color (e.g., #2962ff)');
  }

  if (branding.secondaryColor && !isValidHexColor(branding.secondaryColor)) {
    errors.push('Secondary color must be a valid hex color');
  }

  if (branding.accentColor && !isValidHexColor(branding.accentColor)) {
    errors.push('Accent color must be a valid hex color');
  }

  if (branding.logoUrl && branding.logoUrl.length > 500) {
    errors.push('Logo URL must be 500 characters or less');
  }

  if (branding.paymentInstructions && branding.paymentInstructions.length > 2000) {
    errors.push('Payment instructions must be 2000 characters or less');
  }

  if (branding.emailCcRecipients) {
    if (!Array.isArray(branding.emailCcRecipients)) {
      errors.push('Email CC recipients must be an array');
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      branding.emailCcRecipients.forEach((email, index) => {
        if (!emailRegex.test(email)) {
          errors.push(`Invalid email at position ${index + 1}: ${email}`);
        }
      });
    }
  }

  return { valid: errors.length === 0, errors };
};

/**
 * Get branding configuration
 * Returns default branding merged with any saved configuration
 * @returns {Promise<Object>} Branding configuration
 */
export const getBranding = async () => {
  try {
    const docRef = doc(db, 'settings', 'branding');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      // Merge with defaults to ensure all fields exist
      return { ...DEFAULT_BRANDING, ...data };
    }

    return { ...DEFAULT_BRANDING };
  } catch (error) {
    console.error('Error fetching branding:', error);
    return { ...DEFAULT_BRANDING };
  }
};

/**
 * Update branding configuration
 * @param {Object} brandingData - Partial branding data to update
 * @param {string} userId - ID of user making the update
 * @returns {Promise<Object>} Updated branding configuration
 */
export const updateBranding = async (brandingData, userId) => {
  // Validate input
  const validation = validateBranding(brandingData);
  if (!validation.valid) {
    throw new Error(validation.errors.join(', '));
  }

  // Sanitize HTML fields
  const sanitizedData = {
    ...brandingData,
    paymentInstructions: sanitizeHtml(brandingData.paymentInstructions),
    footerText: sanitizeHtml(brandingData.footerText),
    emailSignature: sanitizeHtml(brandingData.emailSignature)
  };

  // Remove undefined values
  Object.keys(sanitizedData).forEach(key => {
    if (sanitizedData[key] === undefined) {
      delete sanitizedData[key];
    }
  });

  try {
    const docRef = doc(db, 'settings', 'branding');

    await setDoc(docRef, {
      ...sanitizedData,
      updatedAt: serverTimestamp(),
      updatedBy: userId
    }, { merge: true });

    // Return the updated branding
    return await getBranding();
  } catch (error) {
    console.error('Error updating branding:', error);
    throw new Error('Failed to update branding configuration');
  }
};

/**
 * Subscribe to branding changes
 * @param {Function} callback - Callback function receiving branding data
 * @returns {Function} Unsubscribe function
 */
export const subscribeToBranding = (callback) => {
  const docRef = doc(db, 'settings', 'branding');

  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      callback({ ...DEFAULT_BRANDING, ...docSnap.data() });
    } else {
      callback({ ...DEFAULT_BRANDING });
    }
  }, (error) => {
    console.error('Error subscribing to branding:', error);
    callback({ ...DEFAULT_BRANDING });
  });
};

/**
 * Reset branding to defaults
 * @param {string} userId - ID of user making the reset
 * @returns {Promise<Object>} Default branding configuration
 */
export const resetBranding = async (userId) => {
  try {
    const docRef = doc(db, 'settings', 'branding');

    await setDoc(docRef, {
      ...DEFAULT_BRANDING,
      updatedAt: serverTimestamp(),
      updatedBy: userId,
      resetAt: serverTimestamp()
    });

    return { ...DEFAULT_BRANDING };
  } catch (error) {
    console.error('Error resetting branding:', error);
    throw new Error('Failed to reset branding configuration');
  }
};

/**
 * Convert hex color to RGB array for PDF generation
 * @param {string} hex - Hex color string (e.g., "#2962ff")
 * @returns {number[]} RGB array [r, g, b]
 */
export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [41, 98, 255]; // Default blue
};

/**
 * Lighten a hex color by a percentage
 * @param {string} hex - Hex color string
 * @param {number} percent - Percentage to lighten (0-100)
 * @returns {string} Lightened hex color
 */
export const lightenColor = (hex, percent) => {
  const rgb = hexToRgb(hex);
  const lightened = rgb.map(c => Math.min(255, Math.floor(c + (255 - c) * (percent / 100))));
  return `#${lightened.map(c => c.toString(16).padStart(2, '0')).join('')}`;
};

/**
 * Darken a hex color by a percentage
 * @param {string} hex - Hex color string
 * @param {number} percent - Percentage to darken (0-100)
 * @returns {string} Darkened hex color
 */
export const darkenColor = (hex, percent) => {
  const rgb = hexToRgb(hex);
  const darkened = rgb.map(c => Math.max(0, Math.floor(c * (1 - percent / 100))));
  return `#${darkened.map(c => c.toString(16).padStart(2, '0')).join('')}`;
};

export default {
  DEFAULT_BRANDING,
  getBranding,
  updateBranding,
  subscribeToBranding,
  resetBranding,
  validateBranding,
  isValidHexColor,
  sanitizeHtml,
  hexToRgb,
  lightenColor,
  darkenColor
};
